import { Button, Dropdown, MenuProps, Tooltip, message } from 'antd';
import { useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AccountType, KeywordSearchOperator } from '@xbcb/shared-types';
import { DataCyPrefix, ModalKey } from '@xbcb/ui-types';
import { WorkOrderTaskStatus } from '@xbcb/work-order-types';
import { isAglPortal, isMonsEnv } from '@xbcb/ui-env';
import { LOGOUT_ASYNC, setActiveBroker } from 'actions';
import profile from 'assets/profile.svg';
import ChannelConnnectionStatusButton from 'components/ChannelConnectionStatusButton';
import EscalatedToMe from 'components/EscalatedToMe';
import GlobalSearchForm from 'components/GlobalSearchForm';
import HelpVideosButton from 'components/HelpVideosButton';
import Logo from 'components/Logo';
import NewBundleAvailable from 'components/NewBundleAvailable';
import RecentlyViewed from 'components/RecentlyViewed';
import StatusDisplay from 'components/StatusDisplay';
import SwitchUserButton from 'components/SwitchUserButton';
import TeamActionsButton from 'components/TeamActionsButton';
import { getAvatarUrl } from 'libs/getAvatarUrl';
import { getOperatingRegion } from 'libs/getOperatingRegion';
import { useCurrentUser, useCustomsBrokerId, useStatus } from 'libs/hooks';
import {
  FEATURE_RELEASE_KEY,
  restrictedDeployment,
} from 'libs/restrictedDeployment';
import { SEARCH_TASKS } from 'libs/sharedQueries';
import {
  isAssignmentTeamLead,
  isSubjectMatterExpert,
} from 'libs/teamStructureHelpers';
import { useTypedSelector } from 'reducers';
import {
  ConnectionStatusOperatingRegion,
  channelConnectionEnabledRegions,
} from 'types';
import {
  StyledHeader,
  StyledImage,
  StyledImageContainer,
  StyledPrivateActions,
  StyledSpan,
  StyledUserActions,
  StyledUserLabel,
} from './styles';
import { apolloLogout } from 'libs/logout';
import { useModal, safeGetMessage, getEnv } from '@xbcb/ui-utils';
import GeneralConfirmationModal from 'components/GeneralConfirmationModal';
import ActiveBrokerSelect from 'components/ActiveBrokerSelect';
import LanguageSwitcher from 'components/LanguageSwitcher';
import { useBundle } from '@amzn/react-arb-tools';
import { isMigrateWorkflowPage } from '../../libs/isMigrateWorkflowPage';
import { isProject42Enabled } from '../../featureFlags';

const isMons = isMonsEnv();
const { stage } = getEnv();

export type AppHeaderProps = {
  isLoading?: boolean;
};

const AppHeader = ({ isLoading }: AppHeaderProps): React.ReactElement => {
  const [appHeaderBundle] = useBundle('components.AppHeader');
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isVisible = usePageVisibility();
  const { setModal } = useModal(ModalKey.GENERAL_CONFIRMATION);
  const { currentStatus } = useStatus();
  const newBundleAvailable = useTypedSelector(
    ({ ui: { newContent } }) => newContent,
  );
  const [escalatedToMeTaskCount, setEscalatedToMeTaskCount] = useState(0);
  const currentUser = useCurrentUser() ?? {};
  const customsBrokerId = useCustomsBrokerId();
  // Get avatarUrl and company logo url
  const avatarUrl = getAvatarUrl(currentUser.documents) || '';
  const logoUrl = getAvatarUrl(currentUser.account?.documents) || '';
  const operatingRegion = getOperatingRegion(customsBrokerId);
  const channelConnectionButtonEnabledForRegion =
    operatingRegion &&
    channelConnectionEnabledRegions.includes(operatingRegion);
  const onLoginPage = location.pathname === '/login';
  const onMigratePage = isMigrateWorkflowPage();
  const onActionPage = location.pathname === '/action';
  const onboardingPage = ['/pending'].includes(location.pathname);
  const showSearchAndRecent = !(
    isLoading ||
    onLoginPage ||
    onActionPage ||
    onMigratePage
  );
  const dropdownItems: MenuProps['items'] = [
    { key: 'profile', label: safeGetMessage(appHeaderBundle, 'profile') },
    isMons && !isAglPortal() // Can't log out of a Midway session
      ? null
      : { key: 'logout', label: safeGetMessage(appHeaderBundle, 'logout') },
  ];

  const logout = useCallback(async () => {
    dispatch(LOGOUT_ASYNC());
    dispatch(setActiveBroker(undefined));
    // Remove all cached data on logout to avoid cross-session contamination
    await apolloLogout();
    if (isAglPortal()) {
      window.location.assign('/sign-out');
    } else {
      history.push('/login');
    }
  }, [dispatch, history]);

  const handleOptionClick: MenuProps['onClick'] = ({ key }: any) => {
    if (key === 'profile') {
      history.push('/profile');
    } else if (key === 'logout') {
      setModal({
        title: safeGetMessage(appHeaderBundle, 'logout_confirmation'),
        onOk: () => {
          void logout();
        },
        visible: true,
      });
    } else {
      return;
    }
  };

  if (!restrictedDeployment(FEATURE_RELEASE_KEY.PERSONNEL_DASHBOARD)) {
    dropdownItems.unshift({
      key: 'currentStatus',
      label: <StatusDisplay currentStatus={currentStatus} />,
    });
  }

  const shouldSkipPollingTasks =
    !isVisible ||
    (!isAssignmentTeamLead(currentUser) && !isSubjectMatterExpert(currentUser));

  const shipperId = currentUser.account?.id;
  const isP42Enabled = isProject42Enabled({ stage, shipperId });

  // Poll API for tasks currently escalated to me every 5 minutes
  const { data } = useQuery(SEARCH_TASKS, {
    pollInterval: 300000,
    skip: shouldSkipPollingTasks,
    variables: {
      input: {
        searchCriteria: {
          currentlyEscalatedToId: {
            values: [currentUser.id],
            operator: KeywordSearchOperator.EQUALS,
          },
          status: {
            values: WorkOrderTaskStatus.ESCALATED,
            operator: KeywordSearchOperator.EQUALS,
          },
        },
      },
    },
  });
  const currentTaskCount = data?.searchWorkOrderTasks?.totalResults ?? 0;

  useEffect(() => {
    if (currentTaskCount > escalatedToMeTaskCount) {
      void message.info(
        safeGetMessage(appHeaderBundle, 'unresolved_work_order_escalations'),
      );
    }
    setEscalatedToMeTaskCount(currentTaskCount);
  }, [currentTaskCount, escalatedToMeTaskCount]);

  return (
    <StyledHeader className="app-header">
      <GeneralConfirmationModal />
      <Logo url={logoUrl} />
      <div className="main-header">
        {showSearchAndRecent && !onboardingPage ? (
          <>
            <div className="search-recently-viewed-actions">
              <GlobalSearchForm />
              {!isP42Enabled && <RecentlyViewed />}
              {escalatedToMeTaskCount > 0 && (
                <EscalatedToMe taskCount={escalatedToMeTaskCount} />
              )}
            </div>
            <StyledPrivateActions>
              {currentUser.accountType === AccountType.OPERATOR &&
                !isP42Enabled && <ActiveBrokerSelect />}
              {currentUser.accountType === AccountType.OPERATOR &&
                !isP42Enabled && <TeamActionsButton />}
              {
                // TODO: Remove PROD check once UAT complete.
                channelConnectionButtonEnabledForRegion && !isP42Enabled && (
                  <ChannelConnnectionStatusButton
                    operatingRegion={
                      operatingRegion as ConnectionStatusOperatingRegion
                    }
                  />
                )
              }
              <HelpVideosButton />
              {!isP42Enabled && <LanguageSwitcher />}
              <Dropdown
                placement="bottomRight"
                menu={{ items: dropdownItems, onClick: handleOptionClick }}
              >
                <StyledUserActions>
                  {currentUser.originalUser && (
                    <Tooltip
                      placement="leftBottom"
                      title={safeGetMessage(
                        appHeaderBundle,
                        'switch_back_to_operator_view',
                      )}
                    >
                      <SwitchUserButton />
                    </Tooltip>
                  )}
                  {newBundleAvailable && <NewBundleAvailable />}
                  <StyledImageContainer
                    currentStatus={
                      restrictedDeployment(
                        FEATURE_RELEASE_KEY.PERSONNEL_DASHBOARD,
                      )
                        ? undefined
                        : currentStatus
                    }
                  >
                    <StyledImage
                      src={[avatarUrl, profile]}
                      alt={currentUser?.name}
                      data-cy={DataCyPrefix.APP_HEADER_AVATAR}
                    />
                  </StyledImageContainer>
                  <StyledUserLabel>
                    <h3>{currentUser?.account?.name}</h3>
                    <p>{currentUser?.name}</p>
                  </StyledUserLabel>
                </StyledUserActions>
              </Dropdown>
            </StyledPrivateActions>
          </>
        ) : (
          onboardingPage && (
            <div>
              <Button type="text" onClick={() => logout()}>
                <StyledSpan>
                  {safeGetMessage(appHeaderBundle, 'logout')}
                </StyledSpan>
              </Button>
            </div>
          )
        )}
      </div>
    </StyledHeader>
  );
};

export default AppHeader;
